import { useState, useEffect, useRef } from "react";
import CircleInfo from "../design-system/icons/CircleInfo.jsx";
import StarLines from "../design-system/icons/StarLines.jsx";
import CrossLarge from "../design-system/icons/CrossLarge.jsx";
import useAuthedApi from "../hooks/useAuthedApi";
import Alert from "../design-system/alert/Alert.jsx";
import Drawer from "./commons/Drawer.jsx";

const EPC_URL = "https://epc.xenorize.io/";

// ── helpers ────────────────────────────────────────────────────────────────

function fmt(n, d = 2) {
  const v = parseFloat(n);
  return isNaN(v) || !isFinite(v) ? "—" : v.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

const LEVERAGE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// ── Stepper ────────────────────────────────────────────────────────────────

function Stepper({ step }) {
  const labels = ["Trade Config", "Bot Settings", "Confirmation"];
  return (
    <div className="flex items-center gap-0 mb-5">
      {labels.map((label, i) => {
        const n = i + 1;
        const done = step > n;
        const active = step === n;
        return (
          <div key={n} className="flex items-center flex-1 min-w-0">
            <div className="flex items-center gap-1.5 shrink-0">
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0
                  ${done ? "bg-[#09421E] text-white" : active ? "bg-[#1264CE] text-white" : "bg-white text-[#7B7B7B] border border-[#EAEAEA]"}`}
              >
                {done ? "✓" : n}
              </div>
              <span className="text-[11px] font-medium whitespace-nowrap text-[#1C1C1C]">
                {label}
              </span>
            </div>
            {i < 2 && (
              <div className={`flex-1 h-[1.5px] mx-2 ${step > n ? "bg-[#0F6D32]" : "bg-gray-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── InfoIcon ───────────────────────────────────────────────────────────────

function InfoIcon() {
  return <CircleInfo className="w-3.5 h-3.5 text-[#A4A9AC] inline ml-1" />;
}

// ── FieldLabel ─────────────────────────────────────────────────────────────

function FieldLabel({ children, hint, error }) {
  return (
    <label className={`text-sm font-semibold mb-1 block ${error ? "text-red-500" : "text-[#1C1C1C]"}`}>
      {children}
      {hint && <InfoIcon />}
    </label>
  );
}

// ── SuffixInput ────────────────────────────────────────────────────────────

function SuffixInput({ suffix, value, onChange, type = "number", min, max, step, placeholder, readOnly, error }) {
  return (
    <div className={`flex items-center border rounded-xl overflow-hidden bg-white focus-within:ring-2 ${
      error ? "border-red-400 focus-within:ring-red-300" : "border-gray-200 focus-within:ring-[#5B3DF6]"
    } ${readOnly ? "bg-[#F2F4F8]" : ""}`}>
      <input
        type={type}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={onChange}
        placeholder={placeholder || "000"}
        readOnly={readOnly}
        className="flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm text-[#21272A] outline-none"
      />
      {suffix && (
        <span className="px-3 py-2.5 text-xs text-[#697077] font-medium shrink-0 border-l border-gray-100">
          {suffix}
        </span>
      )}
    </div>
  );
}

// ── CoinAvatar ─────────────────────────────────────────────────────────────

function CoinAvatar({ symbol }) {
  const initials = (symbol || "?").slice(0, 3).toUpperCase();
  return (
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-[9px] font-bold text-white shrink-0">
      {initials}
    </div>
  );
}

// ── Step 1 ─────────────────────────────────────────────────────────────────

function Step1({ s1, setS1, tradable, limitData, loadingLimit, limitError, isShort }) {
  const { authedGet } = useAuthedApi();
  const [pairs, setPairs] = useState([]);
  const set = (k) => (e) => setS1((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    authedGet("/api/borrow/pairs/")
      .then((r) => setPairs(r.data.pairs || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const baseCcy = limitData?.base_ccy || (s1.symbol ? s1.symbol.toUpperCase() : "");
  const availableLoan = isShort
    ? parseFloat(limitData?.available_loan || 0)
    : parseFloat(limitData?.available_loan_usdt || 0);
  const availableLoanUsdt = parseFloat(limitData?.available_loan_usdt || 0);
  const balanceBase = parseFloat(limitData?.balance_base || 0);
  const balanceUsdt = parseFloat(limitData?.balance_usdt || 0);
  const hourlyRate = parseFloat(limitData?.hourly_rate || 0);
  const apr = parseFloat(limitData?.apr || 0);

  const zeroBalance = !!(limitData && !limitError && (isShort ? balanceBase : balanceUsdt) === 0);
  const lowBorrow = !!(limitData && !limitError && !zeroBalance && availableLoanUsdt > 0 && availableLoanUsdt < 50);
  // OKX's adjust-leverage-info endpoint can unreliably return 0 for borrowable
  // on some pairs even when real capacity is non-zero. Treat 0 as "pair
  // unavailable right now" and surface it immediately, instead of letting the
  // user find out at submit time. Takes priority over zeroBalance/lowBorrow --
  // except for SHORT when balanceBase is also 0: shorting requires actually
  // holding the base asset, so a 0 balance is the real, relevant reason (the
  // existing zeroBalance alert), not an OKX pair quirk. Don't suppress it.
  const pairUnavailable = !!(
    limitData && !limitError && availableLoan === 0 && !(isShort && balanceBase === 0)
  );

  return (
    <div className="space-y-4">
      {/* Exchange */}
      <div>
        <FieldLabel hint>Exchange</FieldLabel>
        <div className="relative">
          <img src="/icon_okx.svg" alt="OKX" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full pointer-events-none" />
          <select
            className="w-full border border-gray-200 rounded-xl pl-10 pr-3 py-2.5 text-sm text-[#21272A] outline-none focus:ring-2 focus:ring-[#5B3DF6] bg-white"
            value={s1.credentialId}
            onChange={set("credentialId")}
          >
            <option value="">Choose Exchange</option>
            {tradable.map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Borrowable section */}
      <div>
        <p className="text-xs font-semibold text-[#21272A] mb-3">Borrowable</p>

        {/* Leverage */}
        <div className="mb-3">
          <FieldLabel hint>Leverage</FieldLabel>
          <select
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#21272A] outline-none focus:ring-2 focus:ring-[#5B3DF6] bg-white"
            value={s1.leverage}
            onChange={set("leverage")}
          >
            <option value="">Choose Leverage</option>
            {LEVERAGE_OPTIONS.map((l) => (
              <option key={l} value={l}>x{l}</option>
            ))}
          </select>
        </div>

        {/* Trading Pair — dropdown fetched from OKX public pairs */}
        <div className="mb-3">
          <FieldLabel hint>Trading Pair</FieldLabel>
          <select
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#21272A] outline-none focus:ring-2 focus:ring-[#5B3DF6] bg-white"
            value={s1.symbol}
            onChange={set("symbol")}
          >
            <option value="">Choose Trading Pair</option>
            {pairs.map((p) => (
              <option key={p} value={p.split("/")[0]}>{p}</option>
            ))}
          </select>
        </div>

        {/* Borrow info card */}
        <div className="border border-gray-200 rounded-xl p-3 space-y-2.5">
          {/* Warning: pair unavailable (OKX borrowable=0 quirk) — takes priority over zeroBalance/lowBorrow */}
          {pairUnavailable && (
            <Alert
              status="warning"
              alertStyle="lighter"
              size="small"
              dismissible={false}
              message="This pair isn't available for Leverage"
            />
          )}

          {/* Error: zero balance — shown above balance row */}
          {!pairUnavailable && zeroBalance && (
            <Alert
              status="error"
              alertStyle="lighter"
              size="small"
              dismissible={false}
              message={`You have 0.00 ${isShort ? baseCcy : "USDT"} balance in your exchange account. To run the bot, please add fund (${isShort ? baseCcy : "USDT"}) to your exchange account.`}
            />
          )}

          {/* Balance row */}
          <div className="flex items-center gap-2.5">
            <CoinAvatar symbol={s1.symbol} />
            <div>
              <p className="text-[10px] text-[#697077]">Wallet Balance</p>
              <p className="text-sm font-semibold text-[#21272A]">
                {loadingLimit ? "Loading…" : limitData ? `${fmt(isShort ? balanceBase : balanceUsdt, 3)} ${isShort ? baseCcy : "USDT"}` : "-"}
              </p>
            </div>
          </div>

          {/* Warning: low borrow — shown between balance and available-to-borrow */}
          {!pairUnavailable && lowBorrow && (
            <Alert
              status="warning"
              alertStyle="lighter"
              size="small"
              dismissible={false}
              message={`Your current borrowable is lesser than ${fmt(parseFloat(limitData?.available_loan || 0), 0)} ${baseCcy} (=${fmt(availableLoanUsdt)} USDT). To run the bot, please add fund (${isShort ? baseCcy : "USDT"}) to your exchange account.`}
            />
          )}

          {/* Available to borrow — value shared, label/pill wording differs mobile/tablet vs desktop */}
          <div>
            {/* mobile/tablet: "{DIRECTION} - Available to borrow", no buffer hint */}
            <p className="text-[10px] text-[#697077] mb-0.5 desktop:hidden">
              {isShort ? "SHORT" : "LONG"} - Available to borrow
            </p>
            {/* desktop: plain label + buffer hint */}
            <p className="text-[10px] text-[#697077] mb-0.5 hidden desktop:block">Available to borrow</p>
            {limitError ? (
              <p className="text-xs text-red-500">{limitError}</p>
            ) : (
              <p className="text-sm font-semibold text-[#21272A]">
                {loadingLimit
                  ? "Loading…"
                  : limitData
                  ? isShort
                    ? `${fmt(availableLoan, 3)} ${baseCcy}`
                    : `${fmt(availableLoan)} USDT`
                  : "-"}
              </p>
            )}
            <div className="hidden desktop:flex items-center gap-1 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
              <p className="text-[10px] text-[#697077]">After 20% Buffer</p>
            </div>
          </div>

          {/* Borrow rate — mobile/tablet shows daily rate, desktop shows hourly rate */}
          <span className="desktop:hidden inline-flex items-center px-2.5 py-0.5 rounded-full bg-[#302571] text-[10px] text-white font-medium">
            Interest rate:{" "}
            {limitData && !loadingLimit
              ? `${(hourlyRate * 24 * 100).toFixed(6)}%/day (${apr.toFixed(2)}% APR)`
              : "-"}
          </span>
          <span className="hidden desktop:inline-flex items-center px-2.5 py-0.5 rounded-full bg-[#302571] text-[10px] text-white font-medium">
            BORROW RATE{" "}
            {limitData && !loadingLimit
              ? `${(hourlyRate * 100).toFixed(6)}%/hr (${apr.toFixed(2)}% APR)`
              : "-"}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Step 2 ─────────────────────────────────────────────────────────────────

function Step2({ s1, s2, setS2, limitData, isShort }) {
  const [showEpcTip, setShowEpcTip] = useState(true);
  const set = (k) => (e) => setS2((p) => ({ ...p, [k]: e.target.value }));

  const baseCcy = limitData?.base_ccy || s1.symbol.toUpperCase();
  const price = parseFloat(limitData?.price || 0);
  const hourlyRate = parseFloat(limitData?.hourly_rate || 0);
  const leverage = Number(s1.leverage) || 1;

  const availableLoan = isShort
    ? parseFloat(limitData?.available_loan || 0)
    : parseFloat(limitData?.available_loan_usdt || 0);

  const capitalNum = parseFloat(s2.tradingCapital) || 0;
  const tradeSizeNum = parseFloat(s2.tradeSize) || 0;
  const maxSteps = tradeSizeNum > 0 ? Math.max(1, Math.floor(capitalNum / tradeSizeNum)) : 0;

  // Slider pct
  const sliderPct = availableLoan > 0 ? Math.round((capitalNum / availableLoan) * 100) : 0;
  const handleSlider = (e) => {
    const pct = Number(e.target.value);
    const val = (availableLoan * pct) / 100;
    setS2((p) => ({ ...p, tradingCapital: val.toFixed(4) }));
  };

  // Liabilities
  let totalLiabilityUsdt = 0;
  let dailyInterestUsdt = 0;
  if (isShort && price > 0) {
    totalLiabilityUsdt = tradeSizeNum * price * maxSteps;
    dailyInterestUsdt = tradeSizeNum * price * hourlyRate * 24 * maxSteps;
  } else {
    totalLiabilityUsdt = tradeSizeNum * maxSteps;
    dailyInterestUsdt = tradeSizeNum * hourlyRate * 24 * maxSteps;
  }

  // Validation
  const buyTarget = parseFloat(s2.buyTarget) || 0;
  const buyConfirm = parseFloat(s2.buyConfirmation) || 0;
  const sellTarget = parseFloat(s2.sellTarget) || 0;
  const sellConfirm = parseFloat(s2.sellConfirmation) || 0;

  const errors = [];
  if (s2.submitted) {
    if (buyTarget <= 0) errors.push(`Buy Target cannot be blank. Min. Buy Target = 0.01%`);
    else if (buyConfirm >= buyTarget) errors.push(`Buy Confirmation is too large. Max Buy Confirmation = ${buyTarget}%`);
    if (buyConfirm <= 0) errors.push(`Buy Confirmation cannot be blank. Min. Buy Confirmation = 0.01%`);
    if (sellTarget <= 0) errors.push(`Sell Target cannot be blank. Min. Sell Target = 0.01%`);
    else if (sellConfirm >= sellTarget) errors.push(`Sell Confirmation is too large. Max Sell Confirmation = ${sellTarget}%`);
    if (sellConfirm <= 0) errors.push(`Sell Confirmation cannot be blank. Min. Sell Confirmation = 0.01%`);
  }

  const unit = isShort ? baseCcy : "USDT";
  const availableHint = isShort
    ? `${fmt(availableLoan, 3)} ${baseCcy}`
    : `${fmt(availableLoan)} USDT`;

  return (
    <div className="space-y-4">
      {/* EPC Settings tip */}
      {showEpcTip && (
        <div className="flex items-start gap-3 bg-[#DAD6F2] rounded-xl p-3">
          <StarLines className="w-5 h-5 shrink-0 text-[#403297]" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#302571]">EPC Settings</p>
            <p className="text-sm text-[#302571] mt-0.5">
              This is our default settings, you can change the settings or go to EPC to get to know the best settings for your Bot's
            </p>
            <a
              href={EPC_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-block text-sm font-semibold text-[#302571] underline mt-1"
            >
              Go to EPC
            </a>
          </div>
          <button
            onClick={() => setShowEpcTip(false)}
            className="text-[#302571] hover:opacity-70 shrink-0"
            aria-label="Dismiss"
          >
            <CrossLarge width={16} height={16} />
          </button>
        </div>
      )}

      {/* Available to borrow / Trading capital / slider / Trade size+Max steps / Liability — one bordered group, matches Figma */}
      <div className="border border-[#EAEAEA] rounded-[10px] overflow-hidden">
        {/* Available to borrow */}
        <div className="bg-[#F6F7F8] px-3 py-3">
          <p className="text-sm text-[#7B7B7B]">Available to borrow</p>
          <p className="text-sm font-bold text-[#1C1C1C]">{availableHint}</p>
        </div>

        {/* Trading Capital */}
        <div className="bg-white px-2.5 py-3.5">
          <FieldLabel>Trading capital after leverage</FieldLabel>
          <SuffixInput
            suffix={unit}
            value={s2.tradingCapital}
            onChange={set("tradingCapital")}
          />
          {/* Mobile: pill buttons */}
          <div className="flex gap-1.5 mt-2 tablet:hidden">
            {[25, 50, 75, 100].map((pct) => {
              const isActive = availableLoan > 0 && Math.round((capitalNum / availableLoan) * 100) === pct;
              return (
                <button
                  key={pct}
                  type="button"
                  onClick={() => setS2((p) => ({ ...p, tradingCapital: ((availableLoan * pct) / 100).toFixed(4) }))}
                  className={`flex-1 py-1.5 rounded-full text-[11px] font-medium border transition-colors ${
                    isActive
                      ? "bg-[#403297] text-white border-[#403297]"
                      : "bg-white text-[#21272A] border-gray-200 hover:border-[#403297]"
                  }`}
                >
                  {pct}%
                </button>
              );
            })}
          </div>

          {/* Desktop/tablet: slider */}
          <div className="mt-3 hidden tablet:block">
            <div className="relative flex items-center h-4">
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 rounded-full bg-[#F4F4F6] pointer-events-none z-[1]" />
              <div
                className="absolute left-0 top-1/2 -translate-y-1/2 h-1 rounded-full bg-[#403297] pointer-events-none z-[2]"
                style={{ width: `calc(8px + (100% - 16px) * ${sliderPct / 100})` }}
              />
              <div className="absolute left-2 right-2 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none z-[3]">
                {[0, 25, 50, 75, 100].map((p) => (
                  <span key={p} className="w-2 h-2 rounded-full bg-[#D1D1D1] border-2 border-white" />
                ))}
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={sliderPct}
                onChange={handleSlider}
                className="range-thumb-hollow w-full relative z-10"
              />
            </div>
            <div className="flex justify-between text-xs text-[#7B7B7B] mt-1 px-2">
              {["0%", "25%", "50%", "75%", "100%"].map((l) => (
                <span key={l}>{l}</span>
              ))}
            </div>
          </div>

          {/* Trade Size + Max Steps */}
          <div className="grid grid-cols-1 tablet:grid-cols-2 gap-3 mt-4">
            <div>
              <FieldLabel hint>Trade size after leverage</FieldLabel>
              <SuffixInput suffix={unit} value={s2.tradeSize} onChange={set("tradeSize")} />
            </div>
            <div>
              <FieldLabel hint>Max Steps</FieldLabel>
              <SuffixInput
                value={maxSteps > 0 ? maxSteps : "0"}
                suffix="x"
                readOnly
              />
            </div>
          </div>
        </div>

        {/* Liability summary */}
        <div className="bg-[#F6F7F8] px-3 py-3 grid grid-cols-1 tablet:grid-cols-2 gap-3">
          <div>
            <p className="text-sm text-[#7B7B7B] mb-1 flex items-center gap-1">
              TOTAL LIABILITY <InfoIcon />
            </p>
            <p className="text-sm font-semibold text-[#333333]">
              {tradeSizeNum > 0 && capitalNum > 0 && maxSteps > 0 ? `${fmt(totalLiabilityUsdt)} USDT` : "- USDT"}
            </p>
          </div>
          <div>
            <p className="text-sm text-[#7B7B7B] mb-1 flex items-center gap-1">
              EST. DAILY INTEREST <InfoIcon />
            </p>
            <p className="text-sm font-semibold text-[#333333]">
              {tradeSizeNum > 0 && capitalNum > 0 && maxSteps > 0 ? `${fmt(dailyInterestUsdt, 6)} USDT` : "- USDT"}
            </p>
          </div>
        </div>
      </div>

      {/* Bot Settings */}
      <div>
        <p className="text-sm font-semibold text-[#1C1C1C] mb-2">Bot Settings</p>
        {errors.length > 0 && (
          <div className="space-y-1.5 mb-2">
            {errors.map((err, i) => (
              <Alert key={i} status="error" alertStyle="lighter" size="small" dismissible={false} message={err} />
            ))}
          </div>
        )}
        <div className="bg-[#F6F7F8] rounded-2xl p-2.5">
          <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4">
            <div>
              <FieldLabel error={s2.submitted && buyTarget <= 0}>Buy Target</FieldLabel>
              <SuffixInput suffix="%" value={s2.buyTarget} onChange={set("buyTarget")} step="0.01" error={s2.submitted && buyTarget <= 0} />
            </div>
            <div>
              <FieldLabel error={s2.submitted && (buyConfirm <= 0 || buyConfirm >= buyTarget)}>Buy Confirmation</FieldLabel>
              <SuffixInput suffix="%" value={s2.buyConfirmation} onChange={set("buyConfirmation")} step="0.01" error={s2.submitted && (buyConfirm <= 0 || buyConfirm >= buyTarget)} />
            </div>
            <div>
              <FieldLabel error={s2.submitted && sellTarget <= 0}>Sell Target</FieldLabel>
              <SuffixInput suffix="%" value={s2.sellTarget} onChange={set("sellTarget")} step="0.01" error={s2.submitted && sellTarget <= 0} />
            </div>
            <div>
              <FieldLabel error={s2.submitted && (sellConfirm <= 0 || sellConfirm >= sellTarget)}>Sell Confirmation</FieldLabel>
              <SuffixInput suffix="%" value={s2.sellConfirmation} onChange={set("sellConfirmation")} step="0.01" error={s2.submitted && (sellConfirm <= 0 || sellConfirm >= sellTarget)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step 3 ─────────────────────────────────────────────────────────────────

function Step3({ s1, s2, tradable, limitData, isShort, termsChecked, onTermsChange, showTermsError }) {
  const exchange = tradable.find((a) => String(a.id) === String(s1.credentialId));
  const baseCcy = limitData?.base_ccy || s1.symbol.toUpperCase();

  const tradeSizeNum = parseFloat(s2.tradeSize) || 0;
  const capitalNum = parseFloat(s2.tradingCapital) || 0;
  const maxSteps = tradeSizeNum > 0 ? Math.max(1, Math.floor(capitalNum / tradeSizeNum)) : 0;

  const unit = isShort ? baseCcy : "USDT";

  const tradeConfigRows = [
    { label: "Exchange", value: exchange?.label || "—" },
    { label: "Trading Pair", value: `${s1.symbol.toUpperCase()}/USDT` },
    { label: "Leverage", value: `${s1.leverage}x` },
    { label: "Direction", value: isShort ? "SHORT" : "LONG" },
  ];

  const botSettingRows = [
    { label: "Trading Capital", value: `${fmt(capitalNum, 2)} ${unit}` },
    { label: "Trade Size", value: `${fmt(tradeSizeNum, 2)} ${unit}` },
    { label: "Max Steps", value: `${maxSteps}x` },
    { label: "Buy Target", value: `${s2.buyTarget}%` },
    { label: "Buy Confirmation", value: `${s2.buyConfirmation}%` },
    { label: "Sell Target", value: `${s2.sellTarget}%` },
    { label: "Sell Confirmation", value: `${s2.sellConfirmation}%` },
  ];

  return (
    <div className="space-y-4">
      {/* Terms error — shown at top when Continue pressed without ticking */}
      {showTermsError && (
        <Alert
          status="error"
          alertStyle="lighter"
          size="small"
          dismissible={false}
          message="Before continue you required to tick the terms and condition."
        />
      )}

      {/* MMR warning */}
      <div className="flex items-start gap-2 bg-[#DAD6F2] text-[#302571] text-xs rounded-xl px-3 py-2.5">
        <StarLines className="w-4 h-4 shrink-0 text-[#403297]" />
        <span>
          Running margin+leverage bot will require you to keep a maintenance margin ratio
          (MMR) above 100% to avoid liquidation. You can increase your MMR by adding fund
          ({isShort ? (limitData?.base_ccy || "base currency") : "{borrowed currency}"}) to your exchange account.
        </span>
      </div>

      {/* Trade Config */}
      <div className="bg-[#F8F9FB] rounded-xl px-4 divide-y divide-gray-100">
        {tradeConfigRows.map(({ label, value, green }) => (
          <div key={label} className="flex items-center justify-between py-2.5">
            <span className="text-sm text-[#697077]">{label}</span>
            <span className={`text-sm font-semibold ${green ? "text-[#0F6D32] uppercase tracking-wide" : "text-[#21272A]"}`}>
              {value}
            </span>
          </div>
        ))}
      </div>

      {/* BOT Settings */}
      <div>
        <p className="text-[11px] font-bold text-[#21272A] tracking-wider mb-2">Bot Settings</p>
        <div className="bg-[#F8F9FB] rounded-xl px-4 divide-y divide-gray-100">
          {botSettingRows.map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between py-2.5">
              <span className="text-sm text-[#697077]">{label}</span>
              <span className="text-sm font-semibold text-[#21272A]">{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* T&C checkbox */}
      <label className="flex items-start gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={termsChecked}
          onChange={(e) => onTermsChange(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded accent-[#302571] cursor-pointer"
        />
        <span className="text-xs text-[#21272A]">
          By activating this bot, I confirm that I accept Xenorize's Terms and Conditions and acknowledge the risks involved.
        </span>
      </label>
    </div>
  );
}

// ── Defaults ───────────────────────────────────────────────────────────────

const S1_DEFAULTS = { credentialId: "", symbol: "", leverage: "" };
const S2_DEFAULTS = {
  tradingCapital: "",
  tradeSize: "",
  buyTarget: "1",
  buyConfirmation: "0.5",
  sellTarget: "1",
  sellConfirmation: "0.5",
  submitted: false,
};

// ── Main export ────────────────────────────────────────────────────────────

export default function ActivateBotModal({ open, onClose, accounts = [], onStarted, direction = "Long", dryRun = false }) {
  const { authedPost } = useAuthedApi();
  const isShort = direction === "Short";
  const tradable = accounts.filter((a) => a.is_trading_enabled);

  const [step, setStep] = useState(1);
  const [s1, setS1] = useState(S1_DEFAULTS);
  const [s2, setS2] = useState(S2_DEFAULTS);

  const [limitData, setLimitData] = useState(null);
  const [loadingLimit, setLoadingLimit] = useState(false);
  const [limitError, setLimitError] = useState(null);

  const [termsChecked, setTermsChecked] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setStep(1);
      setS1({ ...S1_DEFAULTS, credentialId: tradable[0]?.id || "" });
      setS2({ ...S2_DEFAULTS });
      setLimitData(null);
      setLimitError(null);
      setSubmitError(null);
      setTermsChecked(false);
      setShowTermsError(false);
    }
    prevOpen.current = open;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fetch check-limit
  useEffect(() => {
    if (!s1.credentialId || !s1.symbol || s1.symbol.length < 2 || !s1.leverage) {
      setLimitData(null);
      return;
    }
    let alive = true;
    const t = setTimeout(async () => {
      setLoadingLimit(true);
      setLimitError(null);
      try {
        const res = await authedPost("/api/borrow/check-limit/", {
          symbol: s1.symbol.toUpperCase() + "USDT",
          direction: direction.toUpperCase(),
          credential_id: s1.credentialId,
          leverage: Number(s1.leverage),
          ...(dryRun && { dry_run: true }),
        });
        if (alive) setLimitData(res.data);
      } catch (err) {
        if (alive) {
          setLimitError(err.response?.data?.error || "Failed to load borrow info");
          setLimitData(null);
        }
      } finally {
        if (alive) setLoadingLimit(false);
      }
    }, 700);
    return () => { alive = false; clearTimeout(t); };
  }, [s1.credentialId, s1.symbol, s1.leverage, direction]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── derived ─────────────────────────────────────────────────────────────
  const price = parseFloat(limitData?.price || 0);
  const leverage = Number(s1.leverage) || 1;
  const tradeSizeNum = parseFloat(s2.tradeSize) || 0;
  const capitalNum = parseFloat(s2.tradingCapital) || 0;
  const maxSteps = tradeSizeNum > 0 ? Math.max(1, Math.floor(capitalNum / tradeSizeNum)) : 0;

  const buyTarget = parseFloat(s2.buyTarget) || 0;
  const buyConfirm = parseFloat(s2.buyConfirmation) || 0;
  const sellTarget = parseFloat(s2.sellTarget) || 0;
  const sellConfirm = parseFloat(s2.sellConfirmation) || 0;

  const step2Valid =
    buyTarget > 0 &&
    buyConfirm > 0 &&
    buyConfirm < buyTarget &&
    sellTarget > 0 &&
    sellConfirm > 0 &&
    sellConfirm < sellTarget &&
    tradeSizeNum > 0 &&
    capitalNum > 0 &&
    maxSteps > 0;

  // Mirrors Step1's availableLoan/pairUnavailable logic: isShort reads
  // available_loan (base ccy), else available_loan_usdt.
  const availableLoan = isShort
    ? parseFloat(limitData?.available_loan || 0)
    : parseFloat(limitData?.available_loan_usdt || 0);
  const balanceBase = parseFloat(limitData?.balance_base || 0);
  const pairUnavailable = !!(
    limitData && !limitError && availableLoan === 0 && !(isShort && balanceBase === 0)
  );

  const canStep1 =
    !!s1.credentialId && s1.symbol.length >= 2 && !!s1.leverage && !loadingLimit && !!limitData && !pairUnavailable;

  const canPrimary = step === 1 ? canStep1 : step === 2 ? step2Valid : !submitting;

  // ── submit ───────────────────────────────────────────────────────────────
  const handleNext = async () => {
    if (step === 2) {
      // Mark as submitted to show validation errors
      setS2((p) => ({ ...p, submitted: true }));
      if (!step2Valid) return;
    }
    if (step < 3) {
      setStep((s) => s + 1);
      return;
    }
    // Step 3: check T&C
    if (!termsChecked) {
      setShowTermsError(true);
      return;
    }
    setShowTermsError(false);
    setSubmitting(true);
    setSubmitError(null);

    let collateralPerTrade, initialCapital;
    if (isShort && price > 0) {
      collateralPerTrade = tradeSizeNum / leverage;          // BASE units — engine multiplies by leverage to get notional qty
      initialCapital = (capitalNum * price) / leverage;      // USDT collateral cap for engine capacity check
    } else {
      collateralPerTrade = tradeSizeNum / leverage;          // USDT collateral — engine multiplies by leverage to get notional (tradeSize)
      initialCapital = capitalNum;                           // USDT borrow budget (leveraged capital)
    }

    try {
      const res = await authedPost("/api/borrow/start/", {
        symbol: s1.symbol.toUpperCase() + "USDT",
        direction: direction.toUpperCase(),
        leverage,
        collateral_per_trade: collateralPerTrade,
        initial_capital: initialCapital,
        number_of_grids: maxSteps,
        grid_width: buyTarget,
        buy_flg_return: buyConfirm,
        grid_width_exit: sellTarget,
        exit_flg_return: sellConfirm,
        credential_id: s1.credentialId,
        dry_run: dryRun,
        use_sl: false,
        sl_margin_loss_pct: 0.5,
        doji_threshold_pct: 0.3,
      });
      setShowSuccess(true);
      setTimeout(() => {
        onStarted?.(res.data);
        onClose();
        setShowSuccess(false);
      }, 2000);
    } catch (err) {
      setSubmitError(err.response?.data?.error || "Failed to start bot");
    } finally {
      setSubmitting(false);
    }
  };

  const dirLabel = direction === "Short" ? "Short" : "Long";
  const modalTitle = step < 3
    ? `Activate Xenoarc ${dirLabel} Bot`
    : `Xenoarc-${direction} Activation`;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={modalTitle}
      width="570px"
      footer={
        !showSuccess && (
          <div className="flex items-center gap-3">
            <button
              onClick={step === 1 ? onClose : () => setStep((s) => s - 1)}
              className="flex-1 py-2.5 rounded-xl border border-[#EAEAEA] text-sm font-medium text-[#333333] hover:bg-gray-50 transition-colors"
            >
              {step === 1 ? "Cancel" : "Back"}
            </button>
            <button
              onClick={handleNext}
              disabled={(step !== 3 && !canPrimary) || (step === 3 && !termsChecked && !submitting)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all
                ${((step !== 3 && !canPrimary) || (step === 3 && !termsChecked && !submitting))
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-[#302571] text-white hover:bg-[#241c5c]"}`}
            >
              {step === 3 && submitting ? "Starting…" : "Continue"}
            </button>
          </div>
        )
      }
    >
      {/* Stepper */}
      <Stepper step={step} />

      {/* ponytail: gated on `open` so Step1's pairs-fetch effect doesn't fire
          before the drawer is ever opened; content is empty during the close
          animation as a result — fine, it's a 300ms slide. */}
      {open && showSuccess && (
        <div className="flex flex-col items-center justify-center py-10 gap-4">
          <Alert
            status="success"
            alertStyle="lighter"
            size="large"
            dismissible={false}
            message="Xenorize BOT Active"
            description="Your bot has been activated successfully and will start trading shortly."
          />
        </div>
      )}
      {open && !showSuccess && step === 1 && (
        <Step1
          s1={s1}
          setS1={setS1}
          tradable={tradable}
          limitData={limitData}
          loadingLimit={loadingLimit}
          limitError={limitError}
          isShort={isShort}
        />
      )}
      {open && !showSuccess && step === 2 && (
        <Step2
          s1={s1}
          s2={s2}
          setS2={setS2}
          limitData={limitData}
          isShort={isShort}
        />
      )}
      {open && !showSuccess && step === 3 && (
        <Step3
          s1={s1}
          s2={s2}
          tradable={tradable}
          limitData={limitData}
          isShort={isShort}
          termsChecked={termsChecked}
          onTermsChange={(v) => { setTermsChecked(v); if (v) setShowTermsError(false); }}
          showTermsError={showTermsError}
        />
      )}
      {!showSuccess && submitError && (
        <p className="text-xs text-red-500 mt-3">{submitError}</p>
      )}
    </Drawer>
  );
}
